import { useEffect } from 'react';
import Category from './Category';
import { Category as CategoryType } from 'types/api/envScales/AssessmentScore';
import { SectionCollapse } from 'components/SectionCollapse';
import { useDomainStatus } from './utils';
import {
  useValidations,
  useFieldError
} from 'components/Measure/View/ValidationsContext';

interface DomainProps {
  /** Category list.  */
  categories: CategoryType[];

  /** Domain name. */
  name: string;

  /**
   * Domain identifier (`t_name`), used to key this domain's entry in the
   * server-side validations payload (`environment.domains.<tName>`).
   */
  tName: string;

  /** Assessment ID. */
  assessmentId: number;

  /** If `true`, renders section with top border radius. */
  attached?: boolean;
}

function Domain({
  name,
  tName,
  categories,
  assessmentId,
  attached
}: DomainProps) {
  const localStatus = useDomainStatus(categories);
  const { showErrorStatus, reportFieldResolution } = useValidations();
  const domainPath = `environment.domains.${tName}`;
  const domainError = useFieldError(domainPath);

  const missingScaleIds =
    (domainError?.meta.missing_scale_ids as number[] | undefined) ?? [];

  useEffect(() => {
    reportFieldResolution(domainPath, localStatus === 'completed');
  }, [domainPath, localStatus, reportFieldResolution]);

  const status = showErrorStatus
    ? localStatus === 'completed'
      ? 'completed'
      : 'error'
    : localStatus;

  return (
    <SectionCollapse
      title={name}
      attached={attached}
      status={status}
      closeOthersOnOpen
    >
      {categories.map(category => (
        <Category
          key={category.t_name}
          name={category.name}
          items={category.items}
          assessmentId={assessmentId}
          missingScaleIds={missingScaleIds}
        />
      ))}
    </SectionCollapse>
  );
}

export default Domain;
